import { _, helpers, mui, React, ts, ui } from '_core';

import { prepareEventStudyData } from './helpers';
import { Params } from './types';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  fileEvent: string;
  fileCount: string;
  fullScreen: boolean;
  params: Params;
  title: string;
};

const Chart = ({ id, readOnly, loadWidgetData, fileEvent, fileCount, fullScreen, params, title }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);
  const [showErrorLine, setShowErrorLine] = React.useState(!params.get_histories);

  const [selectedTab, setSelectedTab] = React.useState<'error' | 'tstat' | 'count'>('error');

  const loadData = async (selectedTab: 'error' | 'tstat' | 'count') => {
    try {
      const response = await loadWidgetData({
        data: {
          file: selectedTab == 'count' ? fileCount : fileEvent,
          rename: selectedTab == 'error' ? { avg_cum: 'average_cumulative_return', std_cum: 'error' } : {},
          columns: selectedTab == 'tstat' ? ['days_from_event', 'tstat'] : undefined,
        },
      });
      if (response) setData(response.data);
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (fileEvent || fileCount) loadData(selectedTab);
    return () => setData(null);
  }, [fileEvent, fileCount, selectedTab]);

  const getTabs = () => (
    <mui.core.Box display="flex">
      <mui.core.Box flexGrow={1} display="flex" alignItems="flex-end">
        <mui.core.Tabs
          indicatorColor="primary"
          textColor="primary"
          value={selectedTab}
          onChange={(_e, newTab) => setSelectedTab(newTab)}
        >
          <mui.core.Tab label="Signal Exposure" value="error" />
          <mui.core.Tab label="T-stat" value="tstat" />
          <mui.core.Tab label="Event Count" value="count" />
        </mui.core.Tabs>
      </mui.core.Box>

      {selectedTab == 'error' && (
        <mui.core.Box display="flex" justifyContent="flex-end">
          <mui.core.FormControlLabel
            control={
              <mui.core.Switch size="small" checked={showErrorLine} onChange={() => setShowErrorLine((v) => !v)} />
            }
            label={showErrorLine ? 'Hide error bars' : 'Show error bars'}
          />
        </mui.core.Box>
      )}
    </mui.core.Box>
  );

  const renderChart = () => {
    if (selectedTab == 'error') {
      return (
        <ui.ErrorChart
          id={id}
          data={data}
          fullScreen={fullScreen}
          readOnly={readOnly}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          showError={showErrorLine}
          xAxisField="days_from_event"
          yAxisField="average_cumulative_return"
          xAxisLabel="Days"
          yAxisLabel="Average Difference in Signal Exposure (relative to event date)"
          exportTitle={title}
          bucket={params.get_histories ? 'category' : undefined}
          formatData={(errorData) => prepareEventStudyData(errorData, selectedTab, params.histories) as any}
        />
      );
    }

    if (selectedTab == 'count') {
      return (
        <ui.NestedBarChart
          id={id}
          data={data}
          fullScreen={fullScreen}
          readOnly={readOnly}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          yAxisLabel="Event Count"
          category="build_date"
          rotation={-45}
          exportTitle={title}
          hideLegend
        />
      );
    }

    if (selectedTab == 'tstat' && !params.get_histories) {
      const currentData = prepareEventStudyData(
        data as ts.types.widgets.HeatMapData,
        selectedTab,
        params.histories
      ) as ts.types.widgets.NestedBarData;

      return (
        <ui.NestedBarChart
          id={id}
          data={currentData}
          fullScreen={fullScreen}
          readOnly={readOnly}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          yAxisLabel="T-stat"
          xAxisLabel="Days"
          category="days_from_event"
          exportTitle={title}
          hideLegend
        />
      );
    }

    if (selectedTab == 'tstat' && params.get_histories) {
      const currentData = prepareEventStudyData(data as ts.types.widgets.HeatMapData, selectedTab, params.histories);

      return (
        <ui.HeatmapChart
          id={id}
          data={currentData as ts.types.widgets.HeatMapData}
          xAxisField="days_from_event"
          yAxisField="category"
          exportTitle={title}
          fullScreen={fullScreen}
          readOnly={readOnly}
        />
      );
    }

    return null;
  };

  return (
    <mui.core.Box display="flex" flexDirection="column" height="100%">
      {getTabs()}
      <mui.core.Box flexGrow={1}>{!_.isNil(data) && renderChart()}</mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
